import { zodResolver } from '@hookform/resolvers/zod'
import { getRouteApi } from '@tanstack/react-router'
import { isWithinInterval, startOfDay, endOfDay } from 'date-fns'
import { useEffect, useState } from 'react'
import { type DefaultValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import PaperSheets from '@/assets/images/paper-sheets.svg?react'
import DateRangePicker from '@/components/DateRangePicker/DateRangePicker'
import Divider from '@/components/Divider/Divider'
import Empty from '@/components/Empty/Empty'
import FormField from '@/components/FormField'
import Loading from '@/components/Loading'
import Pagination from '@/components/Pagination/Pagination'
import { AsyncSelect } from '@/components/common/AsyncSelect'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import { FormLayout } from '@/components/common/FormGrid/FormGrid'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { StickyProvider } from '@/contexts/StickyContext'
import useRowSelection from '@/hooks/useRowSelection'
import { toast } from '@/hooks/useToast'
import { studentsQueryOptions } from '@/queries/studentsQueryOptions'
import useSemesters from '@/queries/useSemesters'
import { formatApiDate } from '@/utils/format-date'

import styles from './AbsencesPlanView.module.scss'
import AbsencePlanTable from '../components/AbsencePlanTable'
import AbsenceFormDetails from '../components/AbsencesPlanFormDetails'
import FormFooter from '../components/FormFooter'
import usePlanAbsence from '../mutations/usePlanAbsence'
import useStudentAttendanceIds from '../queries/useStudentAttendaceIds'
import useStudentAbsences from '../queries/useStudentAttendance'
import {
  type AbsencesPlanFormPayload,
  createFormSchema
} from '../utils/create-absence-plan-schema'

const routeApi = getRouteApi('/_auth/absences/plan')

const defaultValues: DefaultValues<AbsencesPlanFormPayload> = {
  student: undefined,
  date: {
    from: undefined,
    to: undefined
  },
  reason: undefined,
  comment: undefined,
  attachments: [],
  lessons: []
}

const AbsencesPlanView = () => {
  const { t } = useTranslation('absences2')
  const { semester } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const { data: semesters } = useSemesters()
  const [page, setPage] = useState(1)

  const selectedSemester = semesters?.getSemester(semester)
  const semesterName = selectedSemester?.name
  const isSemesterArchived = selectedSemester?.status === 'archived'

  const form = useForm<AbsencesPlanFormPayload>({
    resolver: zodResolver(
      createFormSchema(isSemesterArchived, semesters?.getSemester(semester))
    ),
    mode: 'onBlur',
    defaultValues
  })

  const { student, date } = form.watch()
  const studentAndDateSelected = !!(student && date?.from && date?.to)

  const params = {
    studentId: student?.value ? [student?.value] : undefined,
    dateAfter: formatApiDate(date?.from),
    dateBefore: formatApiDate(date?.to)
  }

  const { data: absences, isLoading: isLoadingAbsences } = useStudentAbsences(
    { ...params, page },
    studentAndDateSelected
  )
  const { data: attendanceIds, isLoading: isLoadingAttendanceIds } =
    useStudentAttendanceIds(params, studentAndDateSelected)

  const { mutate: planAbsence, isPending: isPlanAbsenceLoading } =
    usePlanAbsence({
      onSuccess: payload => {
        toast({
          variant: 'success',
          title: t('notification.absence-has-been-planned', {
            count: payload.attendances.length
          })
        })
        navigate({
          to: '/absences/all',
          search: {
            semester: semesters?.currentSemester.id || '',
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE
          }
        })
      },
      onError: () => {
        toast({
          variant: 'error',
          title: t('notification.failed-to-plan-absence')
        })
      }
    })

  const rowSelection = useRowSelection({
    count: absences?.count || 0,
    itemsToSelect: attendanceIds || []
  })

  useEffect(() => {
    form.setValue('lessons', rowSelection.selectedItems)

    if (rowSelection.selectedItems.length > 0) form.trigger('lessons')
  }, [form, rowSelection.selectedItems])

  const handleSubmit = (data: AbsencesPlanFormPayload) => {
    planAbsence({
      semester,
      student: data.student.value,
      attachments: data.attachments,
      attendances: data.lessons,
      notifier_comment: data.comment,
      reason: data.reason,
      send_for_guardian_review: !!data.sendForGuardianReview
    })
  }

  const getDisabledDate = (calendarDay: Date) =>
    !selectedSemester ||
    !isWithinInterval(calendarDay, {
      start: startOfDay(selectedSemester.startDate),
      end: endOfDay(selectedSemester.endDate)
    })

  const resetForm = () => {
    form.resetField('reason')
    form.resetField('comment')
    form.resetField('attachments')
    form.resetField('lessons')
    rowSelection.clearSelection()
    form.clearErrors()
    setPage(1)
  }

  const handlePageChange = (selectedPage: number) => setPage(selectedPage)

  const count = attendanceIds?.length || 0

  return (
    <BasicLayout
      header={t('header.plan-absences')}
      subheader={semesterName}
      moduleName={t('header.academics', { ns: 'common' })}
      breadcrumbs={[
        { title: t('navigation.absences', { ns: 'common' }), to: '/absences' },
        { title: t('navigation.plan-absences', { ns: 'common' }) }
      ]}
      contentClassName={styles.content}
    >
      <Loading spinning={isPlanAbsenceLoading}>
        <FormLayout className={styles.form} form={form} onSubmit={handleSubmit}>
          <header className={styles.header}>
            <FormField
              control={form.control}
              id="student"
              label={t('label.student')}
              required
              name="student"
              render={({ inputProps }) => (
                <AsyncSelect
                  {...inputProps}
                  onChange={value => {
                    if (value && inputProps.onChange) inputProps.onChange(value)
                    resetForm()
                  }}
                  queryOptions={search =>
                    studentsQueryOptions({ search, semesterId: semester })
                  }
                  placeholder={t('placeholder.select-student')}
                />
              )}
            />
            <FormField
              control={form.control}
              id="date"
              label={t('label.date')}
              required
              name="date"
              render={({ inputProps }) => (
                <DateRangePicker
                  {...inputProps}
                  onChange={value => {
                    if (value && inputProps.onChange) inputProps.onChange(value)
                    if (value && value.from && value.to) form.trigger('date')
                  }}
                  clearable
                  onClear={() => {
                    resetForm()
                    if (inputProps.onChange) inputProps.onChange({})
                  }}
                  disabledDate={getDisabledDate}
                  defaultMonth={selectedSemester?.startDate}
                />
              )}
            />
          </header>
          <Divider />
          {!studentAndDateSelected ? (
            <>
              <h2 className={styles.title}>{t('header.lessons')}</h2>
              <FormEmptyState />
              <FormFooter />
            </>
          ) : (
            <div className={styles.tableAndForm}>
              <div className={styles.left}>
                <h2 className={styles.title}>{t('header.lessons')}</h2>
                <div className={styles.tableContainer}>
                  <StickyProvider id="absence-plan-table">
                    <AbsencePlanTable
                      data={absences?.list || []}
                      count={count}
                      isLoading={isLoadingAbsences || isLoadingAttendanceIds}
                      rowSelection={rowSelection}
                    />
                  </StickyProvider>
                </div>
                <div className={styles.pagination}>
                  <Pagination
                    count={count}
                    pageSize={DEFAULT_PAGE_SIZE}
                    id="absences-plan-pagination"
                    page={page}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
              <AbsenceFormDetails form={form} />
            </div>
          )}
        </FormLayout>
      </Loading>
    </BasicLayout>
  )
}

const FormEmptyState = () => {
  const { t } = useTranslation('absences2')

  return (
    <div className={styles.emptyState}>
      <Empty
        title={t('help.no-lessons')}
        description={t('help.select-student-and-date')}
        image={<PaperSheets />}
      />
    </div>
  )
}

export default AbsencesPlanView
